/**
 * Shadow Backup — silent localStorage mirror of trip metadata.
 * Written on every trip save; survives PWA reinstall at the same origin.
 * Photos are NOT included (they live in IndexedDB and are too large for localStorage).
 */

const SHADOW_KEY = 'wayfare_shadow_v1';
const MAX_BYTES = 3 * 1024 * 1024; // 3 MB safe ceiling for localStorage

class ShadowBackup {
  save(trips) {
    if (!Array.isArray(trips) || trips.length === 0) return;
    try {
      const slim = trips.map(t => this._slim(t));
      const record = { version: 'shadow-1.0', savedAt: Date.now(), tripCount: slim.length, trips: slim };
      let json = JSON.stringify(record);

      if (json.length > MAX_BYTES) {
        // Strip notes/reflections to fit under limit
        record.trips = slim.map(t => ({ ...t, notes: [], reflections: [] }));
        record.contentTrimmed = true;
        json = JSON.stringify(record);
      }

      localStorage.setItem(SHADOW_KEY, json);
    } catch (e) {
      console.warn('[ShadowBackup] save failed:', e.message);
    }
  }

  _slim(t) {
    return {
      tripId: t.tripId,
      destination: t.destination,
      dates: t.dates,
      status: t.status,
      stats: t.stats,
      notes: t.notes || [],
      reflections: t.reflections || [],
      challenges: t.challenges || [],
      luggage: t.luggage || [],
      groceries: t.groceries || [],
      gifts: t.gifts || [],
      lodgings: t.lodgings || [],
      privateDocuments: t.privateDocuments || [],
      travelInfo: t.travelInfo || {},
      revealedPois: t.revealedPois || [],
      surfacedPois: t.surfacedPois || [],
      dayClosures: t.dayClosures || {},
      sealedAt: t.sealedAt || null,
      openThreads: t.openThreads || [],
      photos: [],
      createdAt: t.createdAt,
      lastModified: t.lastModified,
    };
  }

  load() {
    try {
      const raw = localStorage.getItem(SHADOW_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  exists() {
    try { return !!localStorage.getItem(SHADOW_KEY); } catch (e) { return false; }
  }

  getInfo() {
    const s = this.load();
    if (!s) return null;
    return {
      savedAt: new Date(s.savedAt),
      tripCount: s.tripCount || s.trips?.length || 0,
      contentTrimmed: !!s.contentTrimmed,
    };
  }

  clear() {
    try { localStorage.removeItem(SHADOW_KEY); } catch (e) {}
  }
}

const shadowBackup = new ShadowBackup();
export default shadowBackup;
